import { KnowledgeArticle, createArticle } from '../types'

export const tasksArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'task-overview',
    title: 'Task Management Overview',
    section: 'tasks',
    summary: 'Tasks track compliance activities with due dates, priorities, and assignees. Auto-generated from control schedules or created manually.',
    content: `
# Task Management Overview

Tasks help you track and manage compliance activities, evidence collection, and remediation work across your organization.

## What Are Tasks?

Tasks are actionable items that need to be completed for compliance. They can be:
- **Auto-generated** - Created automatically from control evidence schedules
- **Manual** - Created by users for specific activities
- **Linked** - Connected to controls, risks, or findings

## Task Properties

### Priority Levels
- **Critical** - Must be completed immediately
- **High** - Important, complete within a week
- **Medium** - Standard priority, complete by due date
- **Low** - Nice to have, can be deferred

### Status Values
- **Open** - Not yet started
- **In Progress** - Work has begun
- **Blocked** - Cannot proceed, needs resolution
- **Completed** - Work is done
- **Cancelled** - Task is no longer needed

## Task Dashboard

The Tasks page shows:
- **My Tasks** - Tasks assigned to you
- **All Tasks** - All tasks in the organization
- **Overdue** - Past due date
- **Due This Week** - Upcoming deadlines

## Filters and Sorting

Filter tasks by:
- Status (open, in progress, completed)
- Priority (critical, high, medium, low)
- Assignee (specific user or unassigned)
- Due date range
- Related control or risk
    `.trim(),
    tags: ['tasks', 'management', 'workflow', 'compliance', 'evidence'],
    relatedPages: ['/tasks'],
    icon: 'CheckSquare',
    order: 1,
  }),

  createArticle({
    id: 'creating-tasks',
    title: 'Creating and Managing Tasks',
    section: 'tasks',
    summary: 'Create tasks manually or link them to controls. Set due dates, assign owners, add notes, and track completion with automatic status updates.',
    content: `
# Creating and Managing Tasks

Learn how to create, assign, and track tasks in the platform.

## Creating a Task

### Manual Task Creation
1. Go to **Tasks**
2. Click **New Task**
3. Fill in task details:
   - Title (clear, actionable description)
   - Description (additional context)
   - Priority level
   - Due date
   - Assignee
4. Link to control/risk (optional)
5. Click **Create Task**

### From Controls
Tasks can be auto-generated based on:
- Evidence collection schedules
- Control review dates
- Audit preparation deadlines

### From Findings
When vulnerabilities or issues are found:
- Remediation tasks are created
- Linked to the finding
- Track fix progress

## Task Actions

### Updating Status
Click on a task to update its status:
1. **Start Work** - Move from Open to In Progress
2. **Complete** - Mark as done
3. **Block** - Flag as blocked with reason
4. **Reopen** - Revert completed task if needed

### Adding Notes
Add notes to track progress:
- Work completed
- Blockers encountered
- Questions or clarifications

### Reassigning
Transfer a task to another user:
1. Open the task
2. Click the assignee field
3. Select new assignee
4. Optional: add note explaining transfer

## Task Notifications

Users receive notifications for:
- New task assignments
- Approaching due dates (7 days, 1 day)
- Overdue tasks
- Status changes
- Comments/mentions

## Best Practices

1. Use clear, actionable titles
2. Set realistic due dates
3. Add context in descriptions
4. Link to related items (controls, risks)
5. Update status promptly
6. Add notes for handoffs
    `.trim(),
    tags: ['tasks', 'workflow', 'assignment', 'notifications', 'tracking'],
    relatedPages: ['/tasks'],
    icon: 'ListTodo',
    order: 2,
  }),

  createArticle({
    id: 'jira-task-sync',
    title: 'Jira Task Synchronization',
    section: 'tasks',
    summary: 'Sync tasks with Jira for teams that use Jira for project management. Two-way sync keeps status and comments updated in both systems.',
    content: `
# Jira Task Synchronization

If your organization uses Jira, you can synchronize GRC tasks with Jira issues for seamless workflow integration.

## Features

### Two-Way Sync
- Create Jira issues from GRC tasks
- Status updates sync both directions
- Comments appear in both systems
- Assignee changes reflected

### Auto-Evidence
When a linked Jira ticket is closed:
- Evidence is automatically created
- Linked to the relevant control
- Includes ticket details and resolution

## Setup Requirements

Before using Jira sync:
1. Connect Atlassian integration (Settings > Integrations)
2. Map a Jira project for GRC tasks
3. Configure default issue type

## Creating Jira Issues

### From a GRC Task
1. Open the task you want to sync
2. Click **Create Jira Issue**
3. Select target project (if multiple configured)
4. Review and adjust Jira fields
5. Click **Create**

### Bulk Creation
1. Select multiple tasks from the task list
2. Click **Actions > Create Jira Issues**
3. Confirm the project and issue type
4. Click **Create All**

## Viewing Synced Tasks

Tasks linked to Jira show:
- Jira issue key (clickable link)
- Jira status badge
- Last sync timestamp

## Sync Behavior

### Status Mapping
| GRC Status | Jira Status |
|------------|-------------|
| Open | To Do |
| In Progress | In Progress |
| Blocked | Blocked |
| Completed | Done |

### What Syncs
- Title/Summary
- Description
- Status
- Comments
- Assignee (if user mapping configured)

### What Doesn't Sync
- Priority (different scales)
- Custom fields (without configuration)
- Attachments (planned)
    `.trim(),
    tags: ['tasks', 'jira', 'sync', 'atlassian', 'integration', 'workflow'],
    relatedPages: ['/tasks', '/settings'],
    icon: 'RefreshCw',
    order: 3,
  }),
]
